import { useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { LuClock, LuMail, LuMapPin, LuPhone } from 'react-icons/lu';

import { Button } from '@mds/shared/ui/shadcn/button';
import { Input } from '@mds/shared/ui/shadcn/input';
import { Textarea } from '@mds/shared/ui/shadcn/textarea';
import { Field } from '@mds/shared/ui/form/field';
import { Form, applyServerErrors, useZodForm } from '@mds/shared/ui/form/form';
import { FormSection } from '@mds/shared/ui/form/form-section';

import IconCard from '@/components/card/icon-card';
import PageHeader from '@/components/heading/page-header';
import { ErrorBlock } from '@/components/states';
import { sendContactInquiry } from '@/features/contact/contact.api';
import { contactSchema } from '@/features/contact/contact.schema';

import { CONTACT } from '@/data';

const band = 'mx-auto max-w-page px-gutter pt-section';

export default function ContactPage() {
  const form = useZodForm(contactSchema, {
    defaultValues: {
      subject: '',
      fullName: '',
      email: '',
      message: '',
    },
  });
  const messageLength = form.watch('message').length;
  /** A failure no field can show. `null` once dismissed or a new send starts. */
  const [formError, setFormError] = useState<unknown>(null);

  const sending = useMutation({
    mutationFn: sendContactInquiry,
    // Validation errors land under their fields. Anything else — the rate
    // limit, the server down, a validation error with no field to point at —
    // is the form's as a whole, in the alert above the button.
    onMutate: () => setFormError(null),
    onError: (error) => {
      if (!applyServerErrors(error, form.setError)) setFormError(error);
    },
  });

  return (
    <>
      <section className={band}>
        <PageHeader
          title="Get in touch"
          description="Booking questions, event proposals, sponsorship or anything else. We answer within one working day."
          // No `meta` row: the place and the hours are on the cards just below.
        />
      </section>

      <section className="mx-auto max-w-page px-gutter pt-block" aria-labelledby="channels-heading">
        {/* IconCard with `ruled` is the spec's ChannelList: a glyph, a label, the
            value set loud, and a quiet line under it saying when to use it.

            Each card is `aligned`: its label, title and description sit on
            rows it shares with the cards beside it. The email is one long word
            and wraps in its quarter; the shared title row grows for all four,
            so every description still starts on one line. */}
        <div className="grid gap-x-card gap-y-2 sm:grid-cols-2 lg:grid-cols-4">
          {/* One number for both: calls and WhatsApp reach the same phone, so
              it is one entry rather than the same digits twice. */}
          <IconCard
            ruled
            icon={<LuPhone />}
            accent="brand"
            eyebrow="Call or WhatsApp"
            title={CONTACT.phone}
            titleSize="xs"
            aligned
            description="The quickest way to ask about a desk, a room or a booking. Call us or send a WhatsApp message."
            quiet
          />
          <IconCard
            ruled
            icon={<LuMail />}
            accent="brand"
            eyebrow="Email"
            title={CONTACT.email}
            titleSize="xs"
            aligned
            description="Proposals, invoices and anything with an attachment."
            quiet
          />
          <IconCard
            ruled
            icon={<LuMapPin />}
            accent="brand"
            eyebrow="Location"
            // The town alone: the full place wrapped to two lines at this size,
            // and the address under it already names the region.
            title={CONTACT.town}
            titleSize="xs"
            aligned
            description={CONTACT.addressLines.join(', ')}
            quiet
          />
          {/* With the other ways to reach us rather than beside the form: when
              the desk is open is part of how to reach it. */}
          <IconCard
            ruled
            icon={<LuClock />}
            accent="brand"
            eyebrow="Opening hours"
            title={CONTACT.openTimes}
            titleSize="xs"
            aligned
            description={CONTACT.openDays}
            quiet
          />
        </div>
      </section>

      {/* The form and the map side by side from `lg`, stacked below it. The
          map stretches to the form's height, so the two end on one line. */}
      <section className="mx-auto grid max-w-page gap-block px-gutter pt-section lg:grid-cols-2">
        <FormSection
          title="Send a message"
          description="Say what it is about so it reaches the right person first time."
        >
          {sending.isSuccess ? (
            // In place of the form rather than a toast: the reference is worth
            // keeping, and a form still sitting there invites sending it twice.
            <div role="status">
              <p className="m-0 font-medium">Thanks, we have your message.</p>
              <p className="mt-2 mb-0 font-mono text-lg">{sending.data.reference}</p>
              <p className="mt-2 mb-0 text-sm text-ink-muted">
                We answer within one working day. Quote the reference above if you follow up.
              </p>
            </div>
          ) : (
            <Form
              form={form}
              onSubmit={(values) => sending.mutate(values)}
              className="flex flex-col gap-5"
            >
              <Field name="subject" label="Subject" required>
                <Input maxLength={120} />
              </Field>

              <div className="grid gap-5 sm:grid-cols-2">
                <Field name="fullName" label="Full name" required>
                  <Input autoComplete="name" />
                </Field>
                <Field name="email" label="Email" required>
                  <Input type="email" autoComplete="email" />
                </Field>
              </div>

              <Field name="message" label="Message" required hint={`${messageLength} / 1000`}>
                <Textarea rows={5} />
              </Field>

              {formError !== null && (
                <ErrorBlock
                  title="We couldn't send your message"
                  error={formError}
                  onDismiss={() => setFormError(null)}
                />
              )}

              <div className="flex flex-wrap items-center gap-4">
                <Button type="submit" size="lg" disabled={sending.isPending}>
                  {sending.isPending ? 'Sending…' : 'Send message'}
                </Button>
                <span className="text-sm text-ink-muted">We answer within one working day.</span>
              </div>
            </Form>
          )}
        </FormSection>

        {/* `title` names the frame for a screen reader, which otherwise
            announces an unnamed document. `min-h-80` keeps it a usable map
            when stacked on a phone, where there is no form beside it to
            stretch against. */}
        <iframe
          title={`Map showing ${CONTACT.addressLines.join(', ')}`}
          src={CONTACT.mapEmbedSrc}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="size-full min-h-80 rounded-card border border-hairline"
        />
      </section>
    </>
  );
}
